/**
 * 비디오별로 예약된 지연 재생 타이머.
 * `deferredRestoreAndPlay`가 세팅하고 `cancelDeferredPlay` 또는 타이머 발화 시 삭제한다.
 */
const playTimers = new WeakMap<HTMLVideoElement, ReturnType<typeof setTimeout>>()

/**
 * 비디오별로 예약된 지연 해제 타이머.
 * `deferredRelease`가 세팅하고 `cancelDeferredRelease` 또는 타이머 발화 시 삭제한다.
 */
const releaseTimers = new WeakMap<HTMLVideoElement, ReturnType<typeof setTimeout>>()

/**
 * 빠른 스크롤 시 짧게 스쳐 지나간 비디오에 대한 디코더 할당 요청을 억제하기 위한 지연 시간(ms).
 * 뷰포트에 이 시간 이상 머문 비디오만 실제 재생을 시작한다.
 */
const DEFAULT_PLAY_DELAY_MS = 150

/**
 * 뷰포트에서 벗어난 비디오를 즉시 release하면 미세 스크롤 중 release/restore 사이클이
 * 빠르게 반복되어 화면 깜빡거림이 발생한다 (CLIENTQA-11749).
 * 이 시간 이상 뷰포트 밖에 머문 비디오만 실제로 리소스를 해제한다.
 */
const DEFAULT_RELEASE_DELAY_MS = 300

/**
 * 비디오 리소스를 즉시 완전히 해제한다 (디바운스 없음).
 *
 * `pause()`만으로는 Android Chromium WebView가 MediaCodec 디코더 슬롯을 OS에 반환하지 않아,
 * 숏폼/프리뷰 비디오가 많을 때 디바이스의 디코더 풀이 포화되고
 * 화면 녹화 등 다른 하드웨어 디코더/인코더 사용이 실패한다 (CLIENTQA-11718).
 *
 * `src` 속성 제거 + `load()` 호출로 디코더/버퍼를 즉시 OS에 반환한다.
 * 복원 시 사용할 원본 URL은 호출부에서 `data-src` 속성에 미리 저장해 둬야 한다.
 *
 * 일반적으로 뷰포트에서 충분히 멀어졌을 때 또는 cleanup(unmount) 상황에서 사용한다.
 * 뷰포트 근처(1화면 이내)에서는 `pauseVideo`를 쓴다.
 * 예약된 지연 재생/해제 타이머가 있으면 함께 취소한다.
 */
export function releaseVideo(v: HTMLVideoElement) {
  cancelDeferredPlay(v)
  cancelDeferredRelease(v)
  v.pause()
  if (v.getAttribute('src')) {
    v.removeAttribute('src')
    v.load()
  }
}

/**
 * 비디오를 일시 정지한다. `src`는 유지하므로 디코더 슬롯은 점유된 상태로 남는다.
 *
 * 뷰포트 기준 1화면 이내에서 이탈한 비디오에 사용한다.
 * src를 유지하므로 다시 진입할 때 `load()` 재호출 없이 `play()`만으로 즉시 재생된다.
 * → 깜빡거림(src 재할당 → loadeddata 대기 구간의 빈 프레임)이 발생하지 않는다.
 *
 * 뷰포트 기준 1화면 이상 벗어난 비디오는 `releaseVideo`로 디코더 슬롯까지 반환해야 한다.
 * 예약된 지연 재생/해제 타이머가 있으면 함께 취소한다.
 */
export function pauseVideo(v: HTMLVideoElement) {
  cancelDeferredPlay(v)
  cancelDeferredRelease(v)
  v.pause()
}

/**
 * 비디오를 즉시 pause하고, 실제 리소스 해제(`src` 제거 + `load()`)는 지연 실행한다.
 *
 * 미세한 스크롤로 Observer가 자주 fire되는 상황에서 release/restore 사이클이
 * 반복되면서 발생하는 화면 깜빡거림을 방지한다 (CLIENTQA-11749).
 *
 * `delayMs` 이내에 뷰포트로 다시 돌아오면 `cancelDeferredRelease`가 호출되어
 * src 재할당 없이 pause 상태에서 바로 play로 전환된다 → **깜빡거림 없음**.
 * delayMs를 넘겨 머물면 실제로 리소스를 해제하여 디코더 슬롯 반환.
 *
 * 이미 예약된 타이머가 있으면 유지한다 (연속 호출에서 지연 시간 리셋 방지).
 */
export function deferredRelease(v: HTMLVideoElement, delayMs = DEFAULT_RELEASE_DELAY_MS) {
  cancelDeferredPlay(v)
  // 재생은 즉시 멈춤 (사운드/프레임 유출 방지)
  v.pause()
  if (releaseTimers.has(v)) return
  const timerId = setTimeout(() => {
    releaseTimers.delete(v)
    if (v.getAttribute('src')) {
      v.removeAttribute('src')
      v.load()
    }
  }, delayMs)
  releaseTimers.set(v, timerId)
}

/**
 * `deferredRelease`로 예약된 리소스 해제가 있으면 취소한다.
 * 짧은 시간 안에 뷰포트로 다시 돌아왔을 때 호출되어 src 재할당 없이 재생을 재개할 수 있게 한다.
 */
export function cancelDeferredRelease(v: HTMLVideoElement) {
  const timerId = releaseTimers.get(v)
  if (timerId !== undefined) {
    clearTimeout(timerId)
    releaseTimers.delete(v)
  }
}

/**
 * `releaseVideo`로 해제된 비디오를 다시 로드 후 재생한다.
 * `data-src`에 저장된 원본 URL을 사용하여 복원한다.
 * `src`가 이미 올바르게 세팅돼 있으면 `load()`는 생략하고 `play()`만 호출한다.
 *
 * src 재할당 → `load()` → 첫 프레임 디코딩 사이에 비디오 요소가 잠깐 빈 프레임을
 * 노출하면서 번쩍거리는 현상이 있어, `loadeddata` 이벤트 전까지 inline opacity:0으로
 * 비디오를 숨긴다. 그 동안 뒤의 poster 이미지 또는 wrapper background가 보이게 된다.
 */
export function restoreAndPlay(v: HTMLVideoElement) {
  // 즉시 실행이므로 예약된 지연 재생이 있다면 취소 (중복 호출 방지)
  cancelDeferredPlay(v)
  // 지연 해제 예약도 취소: deferredRelease로 pause만 걸린 상태에서 돌아온 경우 src가 그대로 유지되어 load() 재호출 없이 play만으로 바로 재생된다
  cancelDeferredRelease(v)
  const src = v.dataset.src
  if (!src) return
  const needsReload = v.getAttribute('src') !== src
  if (needsReload) {
    v.style.opacity = '0'
    const show = () => {
      v.style.opacity = ''
      v.removeEventListener('loadeddata', show)
      v.removeEventListener('error', show)
    }
    v.addEventListener('loadeddata', show)
    // 네트워크/디코딩 에러 시 영구적으로 투명 상태에 머물지 않도록 안전장치
    v.addEventListener('error', show)
    v.setAttribute('src', src)
    v.load()
  }
  v.play().catch(() => {})
}

/**
 * `restoreAndPlay`를 지정 시간 뒤에 지연 실행한다 (디바운스).
 *
 * 뷰포트에 짧게 스쳐 지나간 비디오까지 즉시 디코더를 할당하면
 * 빠른 스크롤 중 할당 요청이 폭주해 MediaCodec 슬롯이 고갈되어
 * 화면 녹화 등이 실패한다 (CLIENTQA-11718).
 *
 * 이 함수는 재생 요청을 큐에 넣고 `delayMs` 이내에 `cancelDeferredPlay` 또는
 * `releaseVideo`가 호출되면 재생 자체를 건너뛴다 → 디코더 할당 요청을 애초에 보내지 않는다.
 * 이미 예약된 타이머가 있으면 유지한다 (연속 호출에서 지연 시간이 리셋되지 않도록).
 */
export function deferredRestoreAndPlay(v: HTMLVideoElement, delayMs = DEFAULT_PLAY_DELAY_MS) {
  if (playTimers.has(v)) return
  const timerId = setTimeout(() => {
    playTimers.delete(v)
    restoreAndPlay(v)
  }, delayMs)
  playTimers.set(v, timerId)
}

/**
 * `deferredRestoreAndPlay`로 예약된 재생이 있으면 취소한다.
 * 없으면 no-op. 뷰포트 이탈 시 호출된다 (release가 자동으로 포함함).
 */
export function cancelDeferredPlay(v: HTMLVideoElement) {
  const timerId = playTimers.get(v)
  if (timerId !== undefined) {
    clearTimeout(timerId)
    playTimers.delete(v)
  }
}
